// Pure drag arithmetic for the split-screen pane divider (#1415). No React, no Apryse, no DOM —
// everything here is a number in, a number out, so it can be unit-tested directly.
//
// The layout being described is `.MultiViewer`'s flex row, in visual order:
//
//     [ pane 1 ][ divider ][ middle dock ][ pane 2 ]
//
// The divider and the middle `Object` dock are fixed-width flex siblings of the two panes, so the
// two panes share only what is left over — `resolvePaneSpace` below. Every function takes that same
// geometry so the forward map (pointer → split) and the inverse (split → where the divider renders)
// can never drift apart: that round trip IS the requirement, since a drag whose handle does not
// land under the cursor is exactly the reported bug.
//
// Why this is ours rather than Apryse's: Apryse's own ResizeBar maps a drag to
// `clientX - webViewerHostLeft` and feeds it in as pane 1's width, which counts every column to
// the LEFT of pane 1 (the `Elevation references` sidebar above all) into pane 1's width, and then
// makes the two pane widths sum to the WHOLE row — over-committing it by the divider plus the dock.
// See the plan for #1415 for the measured breakdown.

/** The flex row the two panes share, measured in CSS pixels. Re-measure per drag — never cache. */
export type TSplitRowGeometry = {
	/**
	 * Client x where pane 1's share of the row begins — the row's left content edge, plus any fixed
	 * inset pane 1's own box puts in front of that share (its border and padding, which the caller
	 * also counts into `reservedWidth`). Everything left of the row, the `Elevation references`
	 * sidebar above all, is already excluded: it is never part of pane 1, which is exactly what
	 * Apryse's own bar got wrong.
	 */
	rowLeft: number
	/** The row's content width — everything the panes, the divider and the dock share. */
	rowWidth: number
	/** The divider's own width. It sits between the panes and belongs to neither. */
	dividerWidth: number
	/**
	 * Everything seated in the row that is not a pane's share: the middle `Object` dock (0 when it
	 * is closed) plus both panes' own border + padding.
	 *
	 * **The dock is on the divider's RIGHT** — the row is `[pane 1][divider][dock][pane 2]`, fixed by
	 * the `order` rules in public/webviewer-quotation.css. That convention is what lets a single
	 * `reservedWidth` describe it: the arithmetic only ever subtracts it from the space the two panes
	 * share, never from the offset the divider renders at. Moving the dock in front of the divider
	 * would need `rowLeft` to absorb it, so change both together or the handle leaves the cursor.
	 */
	reservedWidth: number
	/** The width each pane is kept at as a floor, however far the pointer is dragged. */
	minPaneWidth: number
}

/** Pane 1's share of the pane space. 0.5 is an even split — what entering dual screen starts at. */
export const DEFAULT_SPLIT_FRACTION = 0.5

// The per-pane floor. Deliberately far below Apryse's own hardcoded 350: with the left panel, the
// 240px `Object` dock and the right `Elevation callout` panel all open at ~1440px, two 350px floors
// no longer fit in the row at all, which is what made the drag a dead no-op (#1415 bullet 2). This
// is small enough that a floor is only ever reached at the very end of the travel, and large enough
// that neither pane can be dragged down to an unreadable sliver.
export const MIN_PANE_WIDTH = 160

/**
 * The width the two panes actually share, once the divider and any middle dock are paid for.
 *
 * **An over-committed row clamps to 0**, and so does a non-finite one: when the divider and the dock
 * alone exceed the row there is no space to divide, and a negative "space" would invert every
 * fraction below it. 0 is the value the rest of this module treats as "not divisible" — the bounds
 * collapse onto an even split and the pointer map returns it, so the divider stops rather than
 * running backwards. It is deliberately not the row width, and never negative.
 */
export const resolvePaneSpace = (geometry: TSplitRowGeometry): number => {
	const space = geometry.rowWidth - geometry.dividerWidth - geometry.reservedWidth
	return Number.isFinite(space) && space > 0 ? space : 0
}

/**
 * How far pane 1's fraction may travel before a pane would breach the floor.
 *
 * **Below `paneSpace <= 2 x minPaneWidth` BOTH give way**: the floor drops to half the space, so
 * `min === max === 0.5` and the drag is completely immobile — the same dead drag Apryse's two
 * colliding clamps produced, just at a lower threshold. With `MIN_PANE_WIDTH` that is a pane space
 * of 320px, which the four-column state of the issue (left panel + 240px dock + right panel) reaches
 * at roughly a 1190px window against Apryse's own ~1230px. It is out of reach of the 1440-1500px
 * the issue is about, and an even split is at least a defensible thing to be stuck at rather than a
 * divider pinned to one end, so it is left as it is rather than contorted; the trade is stated here
 * so nobody reads this as "the drag always moves".
 */
export const resolveSplitFractionBounds = (geometry: TSplitRowGeometry): { min: number; max: number } => {
	const paneSpace = resolvePaneSpace(geometry)
	if (paneSpace <= 0) return { min: DEFAULT_SPLIT_FRACTION, max: DEFAULT_SPLIT_FRACTION }
	const requestedFloor = Number.isFinite(geometry.minPaneWidth) ? Math.max(0, geometry.minPaneWidth) : 0
	const floor = Math.min(requestedFloor, paneSpace / 2)
	const min = floor / paneSpace
	return { min, max: 1 - min }
}

/** Hold a fraction inside the bounds above. A non-finite fraction falls back to an even split. */
export const clampSplitFraction = (fraction: number, geometry: TSplitRowGeometry): number => {
	const { min, max } = resolveSplitFractionBounds(geometry)
	if (!Number.isFinite(fraction)) return Math.min(Math.max(DEFAULT_SPLIT_FRACTION, min), max)
	return Math.min(Math.max(fraction, min), max)
}

/**
 * Pointer x → pane 1's fraction of the pane space, clamped.
 *
 * The pointer names where the divider's CENTRE should sit, so pane 1 gets everything to the left of
 * it minus half the grab column — which is what makes the handle track the cursor 1:1 instead of
 * leading or trailing it by 7px.
 */
export const resolveSplitFractionFromPointer = (clientX: number, geometry: TSplitRowGeometry): number => {
	const paneSpace = resolvePaneSpace(geometry)
	if (paneSpace <= 0) return DEFAULT_SPLIT_FRACTION
	const primaryWidth = clientX - geometry.rowLeft - geometry.dividerWidth / 2
	return clampSplitFraction(primaryWidth / paneSpace, geometry)
}

/**
 * Fraction → the client x the divider's centre renders at: the exact inverse of the map above.
 *
 * `resolveDividerCenterX(resolveSplitFractionFromPointer(x, g), g) === x` for every x that does not
 * breach a floor. That identity is the acceptance criterion (AC1) in one line.
 */
export const resolveDividerCenterX = (fraction: number, geometry: TSplitRowGeometry): number =>
	geometry.rowLeft + clampSplitFraction(fraction, geometry) * resolvePaneSpace(geometry) + geometry.dividerWidth / 2

/** The two rendered pane widths. They sum to the pane space exactly — never over- or under-commit. */
export const resolvePaneWidths = (
	fraction: number,
	geometry: TSplitRowGeometry,
): { primaryWidth: number; splitWidth: number } => {
	const paneSpace = resolvePaneSpace(geometry)
	const primaryWidth = clampSplitFraction(fraction, geometry) * paneSpace
	return { primaryWidth, splitWidth: paneSpace - primaryWidth }
}

/**
 * The two `flex-grow` values the stylesheet sizes the panes from.
 *
 * Both panes are `flex: <grow> 1 0px`, so the row's free space — the pane space, since the divider
 * and the dock are its only other bases — is handed out purely by these two numbers, which always
 * sum to 1. A `flex-basis` other than `auto` also takes precedence over `width`, which is what
 * makes the inline `width` Apryse keeps writing onto the pane containers stop mattering: no
 * `!important`, and nothing to fight React over.
 */
export const resolveSplitGrow = (fraction: number): { primaryGrow: number; splitGrow: number } => {
	const primaryGrow = Number.isFinite(fraction)
		? Math.min(Math.max(fraction, 0), 1)
		: DEFAULT_SPLIT_FRACTION
	return { primaryGrow, splitGrow: 1 - primaryGrow }
}
